import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ---- Silent auto-refresh on 401 ----------------------------------------
// When access_token expires (8h) but refresh_token is still valid (14d),
// auto-call /auth/refresh and retry the original request once. Keeps admins
// and users logged in for up to 14 days without manual re-login.
//
// Skips: endpoints that ARE the auth flow itself, to avoid recursion.
const SKIP_REFRESH_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"];

let refreshInFlight = null;

async function doRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = api.post("/auth/refresh").finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthEndpoint = SKIP_REFRESH_PATHS.some((p) => url.includes(p));

    if (status === 401 && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        await doRefresh();
        return api(original);
      } catch (e) {
        // Refresh itself failed — bubble the original 401 up so the UI can send to /login
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Resolve a stored photo reference to a browser-usable URL.
 *  - data:image/... (legacy base64) → returned as-is
 *  - http(s)://...                  → returned as-is
 *  - findyournaseeb/...             → wrapped as /api/files/{path}
 *  Falsy input returns empty string.
 */
export function photoUrl(ref) {
  if (!ref) return "";
  if (typeof ref !== "string") return "";
  if (ref.startsWith("data:") || ref.startsWith("http://") || ref.startsWith("https://") || ref.startsWith("blob:")) return ref;
  const clean = ref.replace(/^\/+/, "");
  return `${API}/files/${clean}`;
}

/** Upload a File/Blob to the backend and return the storage path. */
export async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads/photo", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // {path, size, content_type}
}

